import React, { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Skeleton from '@mui/material/Skeleton';

import { useFeatureManager } from 'hooks/useFeatureManager';
import { Logo }  from 'components/Shared';
import { useNavigationUtils } from '../../hooks';
import { Button } from 'components/Shared/Inputs';

import './DesktopPrimaryNavbar.scss';

interface DesktopPrimaryNavbarProps {}

const KnownPaths = ['/', '/todos', '/clients', '/providers', '/invoicing', '/calendar', '/requests', '/critical_incident/cases', '/critical_incident/referrals'];

export const DesktopPrimaryNavbar: FC<DesktopPrimaryNavbarProps> = (_) => {
    const { t } = useTranslation('navigation', { useSuspense: false });

    const { isListReferralFeatureEnabled, isAdditionalHoursFeatureEnabled } = useFeatureManager();

    const { authenticated, pathRoot, showCalendarTab, showInvoicingTab, showStaffTab, showCriticalIncidentTabs, showCICasesTab, showEAPTabs, loading } = useNavigationUtils();

    const path = KnownPaths.indexOf(pathRoot) !== -1 ? pathRoot : false;

    const renderNavbarTab = (id: string, path: string, props?: any) => (
        <Tab className="navbar_tab" id={id} label={t(id)} value={path} component={Link} to={{pathname: path}} {...props}/>
    );

    const AddNewClientButton = useCallback((props) => {
        const selected = props.isSelected;
        return <Button
            className="add_client_button"
            variant={selected ? "opaque" : "outline"}
            disabled={props.disabled}
            aria-selected={selected}
            id="add-clients"
            component={Link} to="/add-clients"
            role="tab" tabIndex={selected ? 0 : -1}
        >
            <span>{t('add-client', 'Add New Client')}</span>
        </Button>
    }, []);

    return (
        <div className='desktop_primary_navbar'>
            <a href="/"><Logo className="logos_primary_navbar" /></a>
            {
                authenticated &&
                (loading ? <DesktopPrimaryNavbarSkeleton /> : (<>
                    <Tabs className="navbar_tabs" value={path} component="nav" variant="scrollable" scrollButtons="auto">
                        {renderNavbarTab('dashboard', '/', { tabIndex: ((path === '/' || path === false) ? 0 : -1) })}
                        {showCriticalIncidentTabs && renderNavbarTab('referrals', '/critical_incident/referrals')}
                        {showCICasesTab && renderNavbarTab('ci_cases', '/critical_incident/cases')}
                        {showEAPTabs && renderNavbarTab('to-dos', '/todos')}
                        {showEAPTabs && renderNavbarTab('clients', '/clients')}
                        {showInvoicingTab && renderNavbarTab('invoicing', '/invoicing')}
                        {showEAPTabs && isAdditionalHoursFeatureEnabled() && renderNavbarTab('requests', '/requests')}
                        {showStaffTab && renderNavbarTab('staff', '/providers')}
                        {showEAPTabs && showCalendarTab && renderNavbarTab('calendar', '/calendar')}
                        {isListReferralFeatureEnabled() && <AddNewClientButton isSelected={pathRoot === '/add-clients'} />}
                    </Tabs>
                </>))
            }
        </div>
    )
}

export const DesktopPrimaryNavbarSkeleton: React.FC<{}> = () => (
    <div className="navigation_skeleton navbar_tabs">
        <Skeleton variant="text" />
        <Skeleton variant="text" />
        <Skeleton variant="text" />
        <Skeleton variant="text" />
        <Skeleton variant="text" />
    </div>
)
